
/**
 * Visual Product Recommendations JavaScript
 * High-performance recommendation loader:
 * - Single ultra-fast API fetch for 50 similar products
 * - First 12 products rendered in the inline section grid
 * - All 50 products rendered in the full-page overlay for instant display on 'View More' click
 * - 0ms perceived latency, DocumentFragment rendering, native lazy loading
 */

(function () {
    'use strict';

    function formatPrice(priceStr) {
        if (!priceStr) return '';
        const num = parseFloat(priceStr);
        if (isNaN(num)) return priceStr;
        const formatted = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD'
        }).format(num);
        // Strip trailing zeros to match money_without_trailing_zeros
        return formatted.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    }

    function isValidProduct(item) {
        if (!item) return false;
        const handle = String(item.handle || '').trim();
        const imgUrl = String(item.image_url || '').trim();
        return Boolean(handle && handle !== '#' && imgUrl);
    }

    /**
     * Fetch full product data from Shopify's /products/{handle}.json endpoint.
     * Returns the enriched item merged with API data, or the original item on failure.
     */
    async function fetchShopifyProduct(item) {
        const handle = String(item.handle).trim();
        try {
            const resp = await fetch(`/products/${handle}.json`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!resp.ok) return item;
            const data = await resp.json();
            const p = data.product;
            if (!p) return item;

            // Merge Shopify product data onto the API item
            return Object.assign({}, item, {
                title: p.title || item.title,
                product_id: p.id || item.product_id,
                product_type: p.product_type || item.product_type || '',
                vendor: p.vendor || item.vendor || '',
                tags: p.tags ? (typeof p.tags === 'string' ? p.tags.split(', ') : p.tags) : (item.tags || []),
                published_at: p.published_at || item.published_at || '',
                options: p.options || item.options || [],
                price: (p.variants && p.variants[0]) ? (p.variants[0].price || item.price) : item.price,
                compare_at_price: (p.variants && p.variants[0]) ? (p.variants[0].compare_at_price || 0) : (item.compare_at_price || 0),
                image_url: item.image_url || (p.image && p.image.src) || ''
            });
        } catch (e) {
            return item;
        }
    }

    /**
     * Enrich all items with full Shopify product data in parallel (batched).
     * Fetches up to 6 concurrently to avoid overwhelming the browser.
     */
    async function enrichItems(items) {
        const batchSize = 6;
        const enriched = [];
        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            const results = await Promise.all(batch.map(fetchShopifyProduct));
            enriched.push(...results);
        }
        return enriched;
    }

    function createProductCard(item) {
        if (!isValidProduct(item)) return null;

        const handle = String(item.handle).trim();
        const imgUrl = item.image_url;
        const title = item.title || handle;
        const productUrl = `/products/${encodeURIComponent(handle)}`;

        // Price logic
        const regularPrice = parseFloat(item.price) || 0;
        const comparePrice = parseFloat(item.compare_at_price) || 0;
        const hasDiscount = comparePrice > regularPrice && comparePrice > 0;
        let discountPercent = 0;
        if (hasDiscount) {
            discountPercent = Math.round(((comparePrice - regularPrice) / comparePrice) * 100);
        }

        // Swatch info from options
        let swatchesHtml = '';
        if (item.options && Array.isArray(item.options)) {
            item.options.forEach(opt => {
                if (opt && opt.name && opt.values && opt.values.length > 0) {
                    const count = opt.values.length;
                    const label = opt.name + (count !== 1 ? 's' : '');
                    swatchesHtml += `<h5 class="prd-swtch">${count} ${label}</h5>`;
                }
            });
        }

        // Brand check from tags
        let isBrand = false;
        if (item.tags) {
            const tagsArr = Array.isArray(item.tags) ? item.tags : String(item.tags).split(',');
            isBrand = tagsArr.some(t => String(t).trim().includes('INT'));
        }

        // Discount badge text
        let discountBadgeHtml = '';
        if (hasDiscount) {
            const badgeWords = ['Sale', 'Special Offer'];
            const productId = parseInt(item.product_id || '0', 10) || 0;
            const randomIndex = productId % 2;
            discountBadgeHtml = `<span class="exprdi1">${badgeWords[randomIndex]}</span>`;
        }

        // Price HTML (matches pro-tabs-mu structure)
        let priceHtml = '';
        if (regularPrice > 0) {
            if (hasDiscount) {
                priceHtml = `
              <s class="t-subdued">${formatPrice(comparePrice)}</s>
              <span class="displayed-discount">${discountPercent}%
                <span class="sale">${formatPrice(regularPrice)}</span>
              </span>`;
            } else {
                priceHtml = `<span class="regular-price">${formatPrice(regularPrice)}</span>`;
            }
        }

        // Sold out check from tags
        let isSoldOut = false;
        if (item.tags) {
            const tagsArr = Array.isArray(item.tags) ? item.tags : String(item.tags).split(',');
            isSoldOut = tagsArr.some(t => String(t).trim().toLowerCase() === 'out of stock');
        }

        const card = document.createElement('div');
        card.className = 'featured-vs-product-card' + (isSoldOut ? ' is-sold-out' : '');

        card.innerHTML = `
      <a href="${productUrl}">
        <div class="prod-img-wrap loading">
          <img
            loading="lazy"
            decoding="async"
            src="${imgUrl}"
            alt="${title.replace(/"/g, '&quot;')}"
            width="600"
            height="800"
            class="prod-img"
            onload="this.parentElement.classList.remove('loading')"
            onerror="this.closest('.featured-vs-product-card')?.remove();"
          >
          <button class="wishlist-btn" data-product-handle="${handle}" aria-label="Add to wishlist" onclick="event.preventDefault(); event.stopPropagation();">
            <svg class="heart-icon" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                       2 5.42 4.42 3 7.5 3
                       c1.74 0 3.41 0.81 4.5 2.09
                       C13.09 3.81 14.76 3 16.5 3
                       19.58 3 22 5.42 22 8.5
                       c0 3.78-3.4 6.86-8.55 11.54
                       L12 21.35z"/>
            </svg>
          </button>
        </div>
      </a>

      <div class="prod-title">
        <div class="product-item__text ta-c">
          <h4 class="ff-body product-item__product-title fs-product-card-title">
            <a href="${productUrl}">${title}</a>
          </h4>
        </div>
      </div>

      <div class="prod-price">
        <p class="product-item__price">
          ${priceHtml}
        </p>
      </div>

      <div class="prod-swatches">
        ${swatchesHtml}
      </div>

      <div class="extra-prod-infos1">
        <span class="exprdi1">Coupon</span>
        <span class="exprdi1">Free ship</span>
        ${isBrand ? '<span class="exprdi1">Brand</span>' : ''}
        ${discountBadgeHtml}
      </div>
    `;
        return card;
    }

    function setupFullPageExperience(container, allItems) {
        const overlay = container.querySelector('[data-visual-rec-fullpage]');
        const viewMoreBtn = container.querySelector('[data-visual-rec-view-more]');
        if (!overlay || !viewMoreBtn) return;

        // Teleport overlay to body so theme parent containers cannot clip or hide it
        document.body.appendChild(overlay);

        const fullpageGrid = overlay.querySelector('[data-visual-rec-fullpage-grid]');
        const backBtn = overlay.querySelector('[data-visual-rec-back]');
        const closeBtn = overlay.querySelector('[data-visual-rec-close]');
        const currentCountSpan = overlay.querySelector('[data-visual-rec-current-count]');
        const loadedBadge = overlay.querySelector('[data-visual-rec-loaded-badge]');

        const fullPageItems = (allItems || []).slice(0, 50);

        // Render all 50 items into fullpage grid immediately
        if (fullpageGrid) {
            const fragment = document.createDocumentFragment();
            const seenIds = new Set();
            let count = 0;

            fullPageItems.forEach(item => {
                const id = String(item.product_id || item.handle || '');
                if (id && !seenIds.has(id)) {
                    const card = createProductCard(item);
                    if (card) {
                        seenIds.add(id);
                        fragment.appendChild(card);
                        count++;
                    }
                }
            });

            fullpageGrid.innerHTML = '';
            fullpageGrid.appendChild(fragment);

            if (currentCountSpan) {
                currentCountSpan.textContent = count;
            }
            if (loadedBadge) {
                loadedBadge.style.display = count > 0 ? 'inline-block' : 'none';
            }
        }

        let isFullPageOpen = false;

        function openFullPage() {
            isFullPageOpen = true;
            overlay.style.display = 'flex';
            document.body.classList.add('visual-rec-locked');

            requestAnimationFrame(() => {
                overlay.classList.add('is-active');
            });

            if (window.location.hash !== '#similar-products') {
                window.history.pushState({ visualRecOpen: true }, '', '#similar-products');
            }
        }

        function closeFullPage(fromHistory) {
            if (!isFullPageOpen) return;
            isFullPageOpen = false;

            overlay.classList.remove('is-active');
            document.body.classList.remove('visual-rec-locked');

            setTimeout(() => {
                if (!isFullPageOpen) {
                    overlay.style.display = 'none';
                }
            }, 300);

            if (!fromHistory && window.location.hash === '#similar-products') {
                window.history.back();
            }
        }

        // Event listeners
        viewMoreBtn.addEventListener('click', (e) => {
            e.preventDefault();
            openFullPage();
        });

        if (backBtn) {
            backBtn.addEventListener('click', (e) => {
                e.preventDefault();
                closeFullPage(false);
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                closeFullPage(false);
            });
        }

        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && isFullPageOpen) {
                closeFullPage(false);
            }
        });

        window.addEventListener('popstate', () => {
            if (isFullPageOpen) {
                closeFullPage(true);
            }
        });
    }

    async function loadRecommendations(container) {
        const productId = container.dataset.productId;
        const apiUrl = (container.dataset.apiUrl || 'http://localhost:8000').replace(/\/$/, '');
        const grid = container.querySelector('[data-visual-rec-grid]');
        const actions = container.querySelector('[data-visual-rec-actions]');

        if (!productId || !grid) return;

        // Check for mixed content warning early
        if (typeof window !== 'undefined' && window.location.protocol === 'https:' && apiUrl.startsWith('http://localhost')) {
            console.warn('[VisualRec] Warning: Page loaded over HTTPS but API URL is HTTP localhost. Mixed Content may be blocked by browser. Please configure your public HTTPS tunnel URL in section settings.');
        }

        try {
            let items = [];

            // 1. Try single fast fetch with limit=50
            try {
                const endpoint = `${apiUrl}/similar/${productId}?limit=50&offset=0`;
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    items = (data.similar_products || []).filter(isValidProduct);
                } else if (response.status === 422) {
                    // Backend constraint fallback: legacy API has hard cap of le=24 and no offset
                    const fallbackResp = await fetch(`${apiUrl}/similar/${productId}?limit=24`, {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                    });
                    if (fallbackResp.ok) {
                        const data = await fallbackResp.json();
                        items = (data.similar_products || []).filter(isValidProduct);
                    }
                }
            } catch (fetchErr) {
                console.warn('[VisualRec] Primary fetch failed, trying fallback limit=24:', fetchErr);
                try {
                    const fallbackResp = await fetch(`${apiUrl}/similar/${productId}?limit=24`, {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                    });
                    if (fallbackResp.ok) {
                        const data = await fallbackResp.json();
                        items = (data.similar_products || []).filter(isValidProduct);
                    }
                } catch (e) {
                    throw fetchErr;
                }
            }

            const isDesignMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;

            if (!items || items.length === 0) {
                if (isDesignMode) {
                    grid.innerHTML = '<div style="grid-column: 1 / -1; padding: 2.5rem 1rem; text-align: center; color: #555; background: #f9f9f9; border-radius: 8px; border: 1px dashed #ccc;"><strong>Similar</strong><br><span style="font-size: 0.9em; color: #888;">See recommended product to see more</span></div>';
                    return;
                }
                container.style.display = 'none';
                document.dispatchEvent(new CustomEvent('visual-rec:empty', { detail: { productId } }));
                return;
            }

            // 2. Enrich all items with full Shopify product data (price, compare_at_price, tags, options, etc.)
            items = await enrichItems(items);

            // 3. Render initial 12 products in the inline section grid
            const initial12 = items.slice(0, 12);
            const fragment = document.createDocumentFragment();
            initial12.forEach(item => {
                const card = createProductCard(item);
                if (card) fragment.appendChild(card);
            });

            grid.innerHTML = '';
            grid.appendChild(fragment);

            if (actions) {
                actions.style.display = 'flex';
            }

            // 4. Setup full page view with all items (up to 50)
            setupFullPageExperience(container, items);

            document.dispatchEvent(new CustomEvent('visual-rec:loaded', { detail: { productId, count: items.length, items } }));

        } catch (err) {
            console.error('[VisualRec] Could not load recommendations:', err);
            const isDesignMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
            if (isDesignMode) {
                grid.innerHTML = '';
            } else {
                container.style.display = 'none';
            }
            document.dispatchEvent(new CustomEvent('visual-rec:empty', { detail: { productId, error: err } }));
        }
    }

    function init() {
        const containers = document.querySelectorAll('[data-visual-rec-container]');
        containers.forEach(loadRecommendations);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
